import { db } from '../config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { pmt2Service } from './pmt2Service';

// Collection names
const PRODUCT_COLLECTION_BASE = 'pmt2_data';
const QUICKGPO_COLLECTION_BASE = 'pmt2_quickgpoanalysis';

// Interface for the combined data from pmt2_data and pmt2_quickgpoanalysis
export interface LatestGpoResultsData {
  // Current data from pmt2_data
  currentActualGrossProfit: number | null;
  currentGpo: number | null;
  startDate: string | null;
  
  // Data from pmt2_quickgpoanalysis
  previousGpo: number | null;
  daysPassed: number | null;
  grossProfitBefore: number | null;
  grossProfitAfter: number | null;
  changeAmount: number | null;
  changePercent: number | null;
  
  // Calculated values
  totalGpos: number;
  gposPerMonth: number | null;
}

/**
 * Fetches Latest GPO Results data by combining data from pmt2_data and pmt2_quickgpoanalysis
 * 
 * @param companyName The company name
 * @param asin The ASIN to fetch data for
 * @returns Combined data for the Latest GPO Results dashboard
 */
export const getLatestGpoResultsData = async (companyName: string, asin: string): Promise<LatestGpoResultsData | null> => {
  try {
    // Initialize the result object with default values
    const result: LatestGpoResultsData = {
      currentActualGrossProfit: null,
      currentGpo: null,
      startDate: null,
      previousGpo: null,
      daysPassed: null,
      grossProfitBefore: null,
      grossProfitAfter: null,
      changeAmount: null,
      changePercent: null,
      totalGpos: 1, // Default to 1 as per requirements
      gposPerMonth: null
    };
    
    // Fetch product data from pmt2_data
    const productRef = doc(db, PRODUCT_COLLECTION_BASE, companyName, 'products', asin);
    const productDoc = await getDoc(productRef);
    
    if (productDoc.exists()) {
      const productData = productDoc.data();
      
      // Extract current actual gross profit
      if (productData['Gross Profit']) {
        result.currentActualGrossProfit = pmt2Service.parseNumericValue(productData['Gross Profit']);
      }
      
      // Extract current GPO
      if (productData['Gross Profit Override']) {
        result.currentGpo = pmt2Service.parseNumericValue(productData['Gross Profit Override']);
      }
      
      // Extract start date
      if (productData['Start Date']) {
        result.startDate = productData['Start Date'];
      }
    }
    
    // Fetch GPO analysis data from pmt2_quickgpoanalysis
    const gpoRef = doc(db, QUICKGPO_COLLECTION_BASE, companyName, 'entries', asin);
    const gpoDoc = await getDoc(gpoRef);
    
    if (gpoDoc.exists()) {
      const gpoData = gpoDoc.data();
      
      // Extract previous GPO
      if (gpoData.old_gpo) {
        result.previousGpo = pmt2Service.parseNumericValue(gpoData.old_gpo);
      }
      
      // Extract days passed
      if (gpoData.days_passed) {
        result.daysPassed = parseInt(gpoData.days_passed, 10);
      }
      
      // Extract gross profit before
      if (gpoData.gross_profit_before) {
        result.grossProfitBefore = pmt2Service.parseNumericValue(gpoData.gross_profit_before);
      }
      
      // Extract gross profit after
      if (gpoData.gross_profit_after) {
        result.grossProfitAfter = pmt2Service.parseNumericValue(gpoData.gross_profit_after);
      }
      
      // Extract change amount
      if (gpoData['change_$']) {
        result.changeAmount = pmt2Service.parseNumericValue(gpoData['change_$']);
      }
      
      // Extract change percent
      if (gpoData['change_%']) {
        // Remove the % sign and parse as a number
        const percentStr = gpoData['change_%'].toString().replace('%', '');
        result.changePercent = parseFloat(percentStr);
      }
    }
    
    // Calculate GPOs per month
    if (result.startDate) {
      try {
        // Parse the start date
        const startDateParts = result.startDate.split('/');
        if (startDateParts.length === 3) {
          const month = parseInt(startDateParts[0], 10) - 1; // Month is 0-indexed in JS Date
          const day = parseInt(startDateParts[1], 10);
          let year = parseInt(startDateParts[2], 10);
          
          // Handle 2-digit years
          if (year < 100) {
            year = 2000 + year;
          }
          
          const startDate = new Date(year, month, day);
          const today = new Date();
          
          // Calculate days difference
          const diffTime = today.getTime() - startDate.getTime();
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          
          // Calculate GPOs per month (1 GPO / (days / 30))
          if (diffDays > 0) {
            result.gposPerMonth = (1 / (diffDays / 30));
            // Round to 1 decimal place
            result.gposPerMonth = Math.round(result.gposPerMonth * 10) / 10;
          }
        }
      } catch (error) {
        console.error('Error calculating GPOs per month:', error);
      }
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching Latest GPO Results data:', error);
    throw error;
  }
};